// Thin API client for the District backend. Uses REACT_APP_BACKEND_URL
// and always prefixes paths with /api.
//
// CORS-safety: in production the page is served from `district.devnco.in`
// but Create React App freezes REACT_APP_BACKEND_URL into the bundle at
// build time. If that frozen value happens to point at the preview/staging
// host, every API call goes cross-origin and any 4xx/5xx response that
// doesn't carry CORS headers (e.g. a 403 from RBAC) surfaces in the browser
// as a generic "Network error", masking the real status.
//
// Fix: when the build-time backend URL doesn't match the page's own origin,
// fall back to the page's own origin. Emergent's ingress routes `/api/*` to
// the backend pod on the same host, so same-origin calls always work and
// CORS is never required.
const _BUILD_BACKEND = process.env.REACT_APP_BACKEND_URL || '';
const _PAGE_ORIGIN = typeof window !== 'undefined' ? window.location.origin : '';
function _normHost(u) { try { return new URL(u).host; } catch { return ''; } }
const BACKEND = (
  !_BUILD_BACKEND ||
  (_PAGE_ORIGIN && _normHost(_BUILD_BACKEND) && _normHost(_BUILD_BACKEND) !== _normHost(_PAGE_ORIGIN))
) ? _PAGE_ORIGIN : _BUILD_BACKEND;

// Re-export so other modules don't have to repeat the same-origin fallback
// logic. Use `import { BACKEND } from '../api/client';` everywhere.
export { BACKEND };

function authHeader() {
  try {
    const user = JSON.parse(localStorage.getItem('district_user') || 'null');
    const token = localStorage.getItem('district_token');
    const betaTesterId = localStorage.getItem('district_beta_tester_id');
    // The SiteGate identity threads through as `X-Beta-Tester-Id` so
    // Platform-Owner-only endpoints (e.g. /api/beta/testers) can distinguish
    // the owner from a pilot client who happens to have admin creds.
    const headers = betaTesterId ? { 'X-Beta-Tester-Id': betaTesterId } : {};
    if (token) return { ...headers, Authorization: `Bearer ${token}` };
    if (user?.token) return { ...headers, Authorization: `Bearer ${user.token}` };
    return headers;
  } catch { /* ignore */ }
  return {};
}

async function request(path, { method = 'GET', body, signal, headers: extraHeaders = {}, rawBody = false } = {}) {
  const url = `${BACKEND}/api${path}`;
  const headers = { ...authHeader(), ...extraHeaders };
  if (!rawBody) headers['Content-Type'] = headers['Content-Type'] || 'application/json';
  const opts = { method, headers, signal };
  if (body !== undefined) opts.body = rawBody ? body : JSON.stringify(body);
  const res = await fetch(url, opts);
  if (!res.ok) {
    let detail = '';
    let parsed = null;
    try { detail = await res.text(); } catch { /* ignore */ }
    if (detail) {
      try { parsed = JSON.parse(detail); } catch { /* not json */ }
    }
    const err = new Error(`API ${method} ${path} failed: ${res.status} ${detail}`);
    err.status = res.status;
    err.body = parsed;
    throw err;
  }
  if (res.status === 204) return null;
  return res.json();
}

export const api = {
  get: (path) => request(path),
  post: (path, body, headers) => request(path, { method: 'POST', body, headers }),
  patch: (path, body) => request(path, { method: 'PATCH', body }),
  put: (path, body) => request(path, { method: 'PUT', body }),
  delete: (path) => request(path, { method: 'DELETE' }),
};

// ---- Youth Worker ----
export const youthApi = {
  list:           (params = {}) => {
    const qs = new URLSearchParams(params).toString();
    return api.get(`/youth${qs ? `?${qs}` : ''}`);
  },
  profile:        (id) => api.get(`/youth/profile/${id}`),
  attendanceByDate: (date, activity = 'class') => api.get(`/youth/attendance/by-date?date=${date}&activity=${activity}`),
  recordAttendance: (entry) => api.post('/youth/attendance', entry),
  recordAttendanceBulk: (entries) => api.post('/youth/attendance/bulk', entries),
  shifts:         (params = {}) => {
    const qs = new URLSearchParams(params).toString();
    return api.get(`/youth/shifts${qs ? `?${qs}` : ''}`);
  },
  createShift:    (body) => api.post('/youth/shifts', body),
  updateShift:    (id, body) => api.patch(`/youth/shifts/${id}`, body),
  submitShift:    (id) => api.post(`/youth/shifts/${id}/submit`),
  approveShift:   (id, body = {}) => api.post(`/youth/shifts/${id}/approve`, body),
  rejectShift:    (id, body = {}) => api.post(`/youth/shifts/${id}/reject`, body),
  regenerateAiSummary: (id) => api.post(`/youth/profile/${id}/ai-summary`),
  seedDemo:       () => api.post('/youth/seed-demo'),
  payrollExportUrl: () => `${BACKEND}/api/youth/shifts/payroll-export`,
};

// ---- Waypoint Transit Control ----
export const transitApi = {
  routes:        () => api.get('/transit/routes'),
  manifest:      (routeId) => api.get(`/transit/manifest/${routeId}`),
  tap:           (body) => api.post('/transit/tap', body),
  parentComms:   (routeId, limit = 50) => {
    const qs = new URLSearchParams({ ...(routeId ? { routeId } : {}), limit }).toString();
    return api.get(`/transit/parent-comms?${qs}`);
  },
  adjustments:   () => api.get('/transit/adjustments'),
  dispatch:      (adjustmentId, actor = {}) => api.post('/transit/adjustments/dispatch', { adjustmentId, ...actor }),
  resetDemo:     () => api.post('/transit/reset-demo'),
};

// ---- Academic & Educational Progress (PowerSchool + Zendesk ingest) ----
export const academicApi = {
  syncStatus: () => api.get('/academic/sync-status'),
  matrix:     () => api.get('/academic/matrix'),
  timeline:   (youthId) => api.get(`/academic/timeline/${youthId}`),
};

// ---- Family 2Gen Index (household bridge) ----
export const family2genApi = {
  index:        () => api.get('/family2gen/index'),
  household:    (youthId) => api.get(`/family2gen/household/${youthId}`),
  cwfUpdate:    (body) => api.post('/family2gen/cwf-update', body),
  resetDemo:    () => api.post('/family2gen/reset-demo'),
};

// ---- Unified Student Profile 360 (youth) + Neighbor Profile 360 ----
export const profile360Api = {
  /** Youth Worker — unified student profile bundle (youth ID) */
  get:      (youthId) => api.get(`/profile-360/${youthId}`),
  /** Single bundled neighbor profile — snapshots + notes + RSVPs + referrals */
  neighbor: (neighborId) => api.get(`/profile-360/neighbors/${neighborId}`),
};

// ---- Behavior Matrix ("Oops" Ledger) ----
export const behaviorApi = {
  roster:        () => api.get('/behavior/roster'),
  incidents:     (youthId) => api.get(`/behavior/incidents${youthId ? `?youthId=${youthId}` : ''}`),
  createIncident:(body) => api.post('/behavior/incidents', body),
  escalations:   (status = 'open') => api.get(`/behavior/escalations?status=${status}`),
  acknowledge:   (body) => api.post('/behavior/incidents/acknowledge', body),
  seedDemo:      () => api.post('/behavior/seed-demo'),
  resetDemo:     () => api.post('/behavior/reset-demo'),
};

// ---- Community Members + Registration & Attendance (Batch C) ----
export const communityApi = {
  members:       (search = '') => api.get(`/community/members${search ? `?search=${encodeURIComponent(search)}` : ''}`),
  member:        (id) => api.get(`/community/members/${id}`),
  createMember:  (body) => api.post('/community/members', body),
  upgrade:       (id) => api.post(`/community/members/${id}/upgrade-to-neighbor`, {}),
};
export const registrationApi = {
  programs:      () => api.get('/registration/programs'),
  events:        (programId, upcomingOnly = true) => api.get(`/registration/programs/${programId}/events?upcomingOnly=${upcomingOnly}`),
  allEvents:     (programId = '', includePast = false) => api.get(`/registration/events?${programId ? `programId=${programId}&` : ''}includePast=${includePast}`),
  createEvent:   (body) => api.post('/registration/events', body),
  updateEvent:   (eventId, body) => api.patch(`/registration/events/${eventId}`, body),
  cancelEvent:   (eventId) => api.delete(`/registration/events/${eventId}`),
  roster:        (eventId) => api.get(`/registration/events/${eventId}/roster`),
  rsvp:          (eventId, body) => api.post(`/registration/events/${eventId}/register`, body),
  checkIn:       (eventId, registrationId, body = {}) => api.post(`/registration/events/${eventId}/check-in/${registrationId}`, body),
  summary:       () => api.get('/registration/attendance-summary'),
};

// ---- Admin: Toolkit Configurations + Unified Index Form ----
export const toolkitConfigApi = {
  list:    (kind = '') => api.get(`/admin/toolkit-configs${kind ? `?kind=${kind}` : ''}`),
  get:     (id) => api.get(`/admin/toolkit-configs/${id}`),
  create:  (body) => api.post('/admin/toolkit-configs', body),
  update:  (id, body) => api.patch(`/admin/toolkit-configs/${id}`, body),
  delete:  (id) => api.delete(`/admin/toolkit-configs/${id}`),
  uifSchema:   () => api.get('/admin/uif-schema'),
  saveUifSchema: (body) => api.put('/admin/uif-schema', body),
  uifLibrary:  () => api.get('/admin/uif-fields/library'),
};

// ---- Neighbor Profile 360°, Snapshots, Team Activity ----
export const snapshotApi = {
  /** All snapshot responses for a neighbor */
  list:    (neighborId) => api.get(`/snapshots/${neighborId}`),
  /** One snapshot for a (neighbor, toolkit) pair — empty shell if none */
  get:     (neighborId, toolkitId) => api.get(`/snapshots/${neighborId}/${toolkitId}`),
  /** Merge-patch field values. body: { responses, authorId, authorName, authorRole, complete?, completionPct? } */
  patch:   (neighborId, toolkitId, body) => api.patch(`/snapshots/${neighborId}/${toolkitId}`, body),
};

// ---- Eligibility recompute + neighbor prefill ----
export const eligibilityApi = {
  /** Read cached eligibility (lazy-computes on first call). */
  get:     (neighborId) => api.get(`/eligibility/${neighborId}`),
  /** Force a recompute of every program rule against the latest snapshot. */
  recompute: (neighborId) => api.post(`/eligibility/recompute/${neighborId}`, {}),
  /** UIF field-id → value derived from the canonical neighbor record so
   *  the Intake dialog can confirm-not-reask fields the system knows. */
  prefill: (neighborId) => api.get(`/eligibility/${neighborId}/prefill`),
};

// ---- Enrollments (created from the post-intake review/enrollment screen) ----
export const enrollmentApi = {
  list:   (neighborId) => api.get(`/enrollments?neighborId=${encodeURIComponent(neighborId)}`),
  create: (body) => api.post('/enrollments', body),
};

export const memberRegistrationsApi = {
  /** Registrations + upcoming events for a single member (neighbor or community) */
  list:    (memberId, upcomingOnly = false) =>
             api.get(`/registration/members/${memberId}/registrations?upcomingOnly=${upcomingOnly}`),
};

export const teamActivityApi = {
  /** Recent cross-role activity feed (notes / RSVPs / escalations) — PM Dashboard */
  recent:  (limit = 25) => api.get(`/team/activity?limit=${limit}`),
};

// ---- Compliance Document Vault ----
export const vaultApi = {
  index:         () => api.get('/vault/index'),
  documents:     (youthId) => api.get(`/vault/documents/${youthId}`),
  categories:    () => api.get('/vault/categories'),
  upload:        async (formData) => {
    const token = localStorage.getItem('district_token');
    const res = await fetch(`${BACKEND}/api/vault/upload`, {
      method: 'POST',
      body: formData,
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    if (!res.ok) throw new Error(await res.text());
    return res.json();
  },
  manualEntry:   (body) => api.post('/vault/manual-entry', body),
  downloadUrl:   (docId) => `${BACKEND}/api/vault/download/${docId}`,
  seedDemo:      () => api.post('/vault/seed-demo'),
  resetDemo:     () => api.post('/vault/reset-demo'),
  // Testing-report inbox
  testingInbox:  () => api.get('/vault/testing-reports'),
  testingConfig: () => api.get('/vault/testing-reports/inbox-config'),
  testingIngest: async (formData) => {
    const token = localStorage.getItem('district_token');
    const res = await fetch(`${BACKEND}/api/vault/testing-reports/ingest`, {
      method: 'POST',
      body: formData,
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    if (!res.ok) throw new Error(await res.text());
    return res.json();
  },
};

// ---- Youth payroll (extends youthApi) ----
export const payrollApi = {
  exportUrl: (format = 'generic') => `${BACKEND}/api/youth/shifts/payroll-export?format=${format}`,
  // Fetches the CSV with the JWT attached (window.open won't carry the
  // Authorization header) and triggers a real <a download> click.
  downloadCsv: async (format = 'generic') => {
    const res = await fetch(`${BACKEND}/api/youth/shifts/payroll-export?format=${format}`, {
      method: 'GET',
      headers: authHeader(),
    });
    if (!res.ok) throw new Error(`Payroll export failed: ${res.status}`);
    const disp = res.headers.get('Content-Disposition') || '';
    const m = /filename="?([^";]+)"?/.exec(disp);
    const filename = m ? m[1] : `payroll_${format}.csv`;
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1500);
    return filename;
  },
  rateShift: (shiftId, body) => api.post(`/youth/shifts/${shiftId}/rate`, body),
};

// ---- Auth ----
export const authApi = {
  staffLogin: (email, password) => api.post('/auth/staff/login', { email, password }),
  neighborLogin: (phone, otp) => api.post('/auth/neighbor/login', { phone, otp }),
  adminLogin: (email, password) => api.post('/auth/admin/login', { email, password }),
  demoDirectory: () => api.get('/auth/demo-directory'),
};

// ---- Team Wellness (PM-only) ----
export const wellnessApi = {
  team: () => api.get('/team-wellness'),
  checkIn: (cm_id, message) => api.post('/team-wellness/check-in', { cm_id, message }),
  listCheckIns: (cm_id) => api.get(`/team-wellness/check-ins${cm_id ? `?cm_id=${cm_id}` : ''}`),
  ackReplies: (cm_id) => api.post('/team-wellness/replies/ack', cm_id ? { cm_id } : {}),
  // CM-side inbox + actions
  inbox: () => api.get('/team-wellness/inbox'),
  ack: (id) => api.post(`/team-wellness/${id}/ack`, {}),
  reply: (id, message) => api.post(`/team-wellness/${id}/reply`, { message }),
};

// ---- Catalog (static reference) ----
export const catalogApi = {
  staff: () => api.get('/catalog/staff'),
  neighbors: () => api.get('/catalog/neighbors'),
  tenants: () => api.get('/catalog/tenants'),
  // Programs / Toolkits / per-Program Indicators (B1+B2+B4)
  listPrograms: (activeOnly = false) => api.get(`/catalog/programs${activeOnly ? '?active_only=true' : ''}`),
  getProgram: (id) => api.get(`/catalog/programs/${id}`),
  listToolkits: () => api.get(`/catalog/toolkits`),
  listProgramIndicators: (programId) => api.get(`/catalog/programs/${programId}/indicators`),
  createProgramIndicator: (programId, body) => api.post(`/catalog/programs/${programId}/indicators`, body),
  updateProgramIndicator: (programId, id, body) => api.patch(`/catalog/programs/${programId}/indicators/${id}`, body),
  deleteProgramIndicator: (programId, id) => api.delete(`/catalog/programs/${programId}/indicators/${id}`),
  testProgramIndicator: (programId, id, body) => api.post(`/catalog/programs/${programId}/indicators/${id}/test`, body),
  indicatorEngineSchema: () => api.get(`/catalog/indicator-engine/schema`),
};

// Outcome events
export const outcomeEventsApi = {
  list: (filters = {}) => {
    const qs = Object.entries(filters).filter(([, v]) => v).map(([k, v]) => `${k}=${encodeURIComponent(v)}`).join('&');
    return api.get(`/outcome-events${qs ? '?' + qs : ''}`);
  },
  create: (body) => api.post('/outcome-events', body),
};

// Funder ↔ Program indicator linkage (C3)
export const funderIndicatorApi = {
  link: (grantId, indicatorId, programIndicatorId) =>
    api.patch(`/grants/${grantId}/indicators/${indicatorId}/link-program-indicator`,
              { program_indicator_id: programIndicatorId }),
  computedValue: (grantId, indicatorId) =>
    api.get(`/grants/${grantId}/indicators/${indicatorId}/computed-value`),
};

// ---- Cross-role writable collections ----
const q = (obj) => {
  const parts = Object.entries(obj || {}).filter(([, v]) => v !== undefined && v !== null && v !== '');
  if (!parts.length) return '';
  return '?' + parts.map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(v)}`).join('&');
};

export const notesApi = {
  list: (filters) => api.get(`/notes${q(filters)}`),
  create: (note) => api.post('/notes', note),
};

export const messagesApi = {
  list: (filters) => api.get(`/messages${q(filters)}`),
  create: (msg) => api.post('/messages', msg),
  markRead: (id) => api.patch(`/messages/${id}/read`),
};

export const referralsApi = {
  list: (filters) => api.get(`/referrals${q(filters)}`),
  create: (ref) => api.post('/referrals', ref),
  update: (id, patch) => api.patch(`/referrals/${id}`, patch),
};

export const approvalsApi = {
  list: (filters) => api.get(`/approvals${q(filters)}`),
  create: (apr) => api.post('/approvals', apr),
  update: (id, patch) => api.patch(`/approvals/${id}`, patch),
};

export const goalsApi = {
  list: (filters) => api.get(`/goals${q(filters)}`),
  create: (goal) => api.post('/goals', goal),
  update: (id, patch) => api.patch(`/goals/${id}`, patch),
};

export const appointmentsApi = {
  list: (filters) => api.get(`/appointments${q(filters)}`),
  create: (appt) => api.post('/appointments', appt),
  update: (id, patch) => api.patch(`/appointments/${id}`, patch),
};

export const statusUpdatesApi = {
  list: (filters) => api.get(`/status-updates${q(filters)}`),
  create: (stu) => api.post('/status-updates', stu),
};

export const escalationsApi = {
  list: (filters) => api.get(`/escalations${q(filters)}`),
  create: (esc) => api.post('/escalations', esc),
  update: (id, patch) => api.patch(`/escalations/${id}`, patch),
};

export const documentsApi = {
  list: (filters) => api.get(`/documents${q(filters)}`),
  create: (doc) => api.post('/documents', doc),
};

// ---- Secure file download ----
// Browser <a href> can't carry the JWT, so we fetch the file with auth, turn
// it into a blob URL, and open it. Caller can pass `download=true` to force
// a save dialog instead of opening a preview tab.
export const filesApi = {
  open: async (storagePath, displayName, { download = false } = {}) => {
    if (!storagePath) throw new Error('Missing storage path');
    const url = `${BACKEND}/api${storagePath.startsWith('/') ? '' : '/files/'}${storagePath}`;
    const res = await fetch(url, { headers: authHeader() });
    if (!res.ok) {
      let detail = '';
      try { detail = await res.text(); } catch { /* ignore */ }
      throw new Error(`Download failed: ${res.status} ${detail}`);
    }
    const blob = await res.blob();
    const objectUrl = URL.createObjectURL(blob);
    if (download) {
      const a = document.createElement('a');
      a.href = objectUrl;
      a.download = displayName || storagePath.split('/').pop() || 'download';
      document.body.appendChild(a);
      a.click();
      a.remove();
    } else {
      window.open(objectUrl, '_blank', 'noopener,noreferrer');
    }
    // Give the browser a moment to start the request before revoking
    setTimeout(() => URL.revokeObjectURL(objectUrl), 30_000);
  },
};

export const timelineApi = {
  forNeighbor: (id) => api.get(`/neighbors/${id}/timeline`),
};

// ---- Grants Module (Stage 1 read + Stage 2 dashboard) ----
export const grantsApi = {
  // Stage 1
  list: (filters) => api.get(`/grants${q(filters)}`),
  detail: (id) => api.get(`/grants/${id}`),
  years: (id) => api.get(`/grants/${id}/years`),
  indicators: (id) => api.get(`/grants/${id}/indicators`),
  reportingSchedule: (id) => api.get(`/grants/${id}/reporting-schedule`),
  compliance: (id) => api.get(`/grants/${id}/compliance`),
  budget: (id) => api.get(`/grants/${id}/budget`),
  funders: () => api.get('/funders'),
  subGrantees: () => api.get('/sub-grantees'),
  focusAreas: () => api.get('/focus-areas'),
  // Stage 2 dashboard
  summary: () => api.get('/grants-dashboard/summary'),
  sidebarCounts: (recipientId) => api.get(`/grants-dashboard/sidebar-counts${q({ recipient_id: recipientId })}`),
  reportingCalendar: () => api.get('/grants-dashboard/reporting-calendar'),
  pacing: () => api.get('/grants-dashboard/pacing'),
  funderPortfolio: (year) => api.get(`/grants-dashboard/funder-portfolio${q({ year })}`),
  complianceHealth: () => api.get('/grants-dashboard/compliance-health'),
  dataQuality: () => api.get('/grants-dashboard/data-quality'),
  auditLog: (limit) => api.get(`/grants-dashboard/audit-log${q({ limit })}`),
  notifications: (recipientId) => api.get(`/grants-dashboard/notifications${q({ recipient_id: recipientId })}`),
  // Stage 3 — screen-backing reads
  stories: (id, filters) => api.get(`/grants/${id}/stories${q(filters)}`),
  programs: (id) => api.get(`/grants/${id}/programs`),
  contributors: (id, period) => api.get(`/grants/${id}/contributors${q({ period })}`),
  activity: (id, filters) => api.get(`/grants/${id}/activity${q(filters)}`),
  reportingCalendar2: (filters) => api.get(`/reporting-calendar${q(filters)}`),
  // Stage 3 — writes
  patchGrant: (id, body) => api.patch(`/grants/${id}`, body),
  patchTarget: (targetId, body) => api.patch(`/indicator-targets/${targetId}`, body),
  approveStory: (storyId, body) => api.patch(`/grants/stories/${storyId}/approve`, body),
  deleteStory: (storyId) => api.delete(`/grants/stories/${storyId}`),
  markComplianceReviewed: (reqId) => api.post(`/compliance/${reqId}/mark-reviewed`, {}),
  recordDisbursement: (disbId, body) => api.post(`/disbursements/${disbId}/record`, body),
  // Stage 6 — Reporting / Compliance / Disbursement CRUD
  createReportingPeriod: (gid, body) => api.post(`/grants/${gid}/reporting-periods`, body),
  patchReportingPeriod: (gid, rid, body) => api.patch(`/grants/${gid}/reporting-periods/${rid}`, body),
  deleteReportingPeriod: (gid, rid) => api.delete(`/grants/${gid}/reporting-periods/${rid}`),
  // Phase 1 — Report Builder
  reportBuilderData: (rpId) => api.get(`/reporting-periods/${rpId}/builder-data`),
  reportDemographics: (rpId, range) => api.get(`/reporting-periods/${rpId}/demographics${q({ range })}`),
  reportGeography: (rpId) => api.get(`/reporting-periods/${rpId}/geography`),
  saveReportValues: (rpId, body) => api.put(`/reporting-periods/${rpId}/values`, body),
  transitionReportingPeriod: (rpId, body) => api.patch(`/reporting-periods/${rpId}/status`, body),
  createComplianceItem: (gid, body) => api.post(`/grants/${gid}/compliance-items`, body),
  patchComplianceItem: (gid, cid, body) => api.patch(`/grants/${gid}/compliance-items/${cid}`, body),
  deleteComplianceItem: (gid, cid) => api.delete(`/grants/${gid}/compliance-items/${cid}`),
  createDisbursementSchedule: (gid, body) => api.post(`/grants/${gid}/disbursement-schedules`, body),
  patchDisbursementSchedule: (gid, did, body) => api.patch(`/grants/${gid}/disbursement-schedules/${did}`, body),
  deleteDisbursementSchedule: (gid, did) => api.delete(`/grants/${gid}/disbursement-schedules/${did}`),
  // Stage 4 — Grantor-side
  subAwards: (filters) => api.get(`/sub-awards${q(filters)}`),
  subAwardDetail: (id) => api.get(`/sub-awards/${id}`),
  subAwardIndicators: (id, period) => api.get(`/sub-awards/${id}/indicators${q({ period })}`),
  subAwardSubmissions: (id) => api.get(`/sub-awards/${id}/submissions`),
  subAwardSubmissionDetail: (id, sid) => api.get(`/sub-awards/${id}/submissions/${sid}`),
  setVerdict: (id, sid, vid, body) =>
    api.post(`/sub-awards/${id}/submissions/${sid}/values/${vid}/verdict`, body),
  checkChecklist: (id, sid, code, body) =>
    api.post(`/sub-awards/${id}/submissions/${sid}/checklist/${code}/check`, body),
  markSection: (id, sid, body) =>
    api.post(`/sub-awards/${id}/submissions/${sid}/section-reviewed`, body),
  patchReviewNotes: (id, sid, body) =>
    api.patch(`/sub-awards/${id}/submissions/${sid}/notes`, body),
  acceptSubmission: (id, sid, body) =>
    api.post(`/sub-awards/${id}/submissions/${sid}/accept`, body),
  requestRevisions: (id, sid, body) =>
    api.post(`/sub-awards/${id}/submissions/${sid}/request-revisions`, body),
  createSubmission: (id, body) => api.post(`/sub-awards/${id}/submissions`, body),
  holdDisbursement: (disbId, body) => api.post(`/disbursements/${disbId}/hold`, body),
  // Sub-grantees
  subGrantees: (filters) => api.get(`/sub-grantees-directory${q(filters)}`),
  subGranteeDetail: (id) => api.get(`/sub-grantees-directory/${id}`),
  subGranteeSubAwards: (id) => api.get(`/sub-grantees-directory/${id}/sub-awards`),
  subGranteeDocuments: (id) => api.get(`/sub-grantees-directory/${id}/documents`),
  attachDocument: (id, body) => api.post(`/sub-grantees-directory/${id}/documents`, body),
  deleteDocument: (id, docId) => api.delete(`/sub-grantees-directory/${id}/documents/${docId}`),
  patchSubGrantee: (id, body) => api.patch(`/sub-grantees-directory/${id}`, body),
  samsRecheck: (id) => api.post(`/sub-grantees-directory/${id}/sams-recheck`, {}),
  // Stage 4.5
  createSubAward: (body) => api.post(`/sub-awards`, body),
  patchSubAward: (id, body) => api.patch(`/grants/${id}`, body),
  createSubGrantee: (body) => api.post(`/sub-grantees-directory`, body),
  addContact: (orgId, body) => api.post(`/sub-grantees-directory/${orgId}/contacts`, body),
  removeContact: (orgId, contactId) => api.delete(`/sub-grantees-directory/${orgId}/contacts/${contactId}`),
  linkProgram: (grantId, body) => api.post(`/grants/${grantId}/program-links`, body),
  patchProgramLink: (grantId, linkId, body) => api.patch(`/grants/${grantId}/program-links/${linkId}`, body),
  unlinkProgram: (grantId, linkId) => api.delete(`/grants/${grantId}/program-links/${linkId}`),
  takeOverReview: (subAwardId, sid) => api.post(`/sub-awards/${subAwardId}/submissions/${sid}/take-over`, {}),
  // Stage 5 (slice b)
  getReportBuilder: (grantId, sid) => api.get(`/grants/${grantId}/reports/${sid}`),
  saveDraft: (grantId, sid, body) => api.patch(`/grants/${grantId}/reports/${sid}/draft`, body),
  submitReport: (grantId, sid, body) => api.post(`/grants/${grantId}/reports/${sid}/submit`, body),
  listGrantSubmissions: (grantId) => api.get(`/grants/${grantId}/reporting-submissions`),
  listSubmittedReports: () => api.get(`/grants/reports/submitted`),
  // Stage 6 — Funder CRUD + Wizards + Allocations
  createFunder: (body) => api.post('/funders', body),
  patchFunder: (id, body) => api.patch(`/funders/${id}`, body),
  deleteFunder: (id) => api.delete(`/funders/${id}`),
  funderGrants: (id) => api.get(`/funders/${id}/grants`),
  grantWizard: (body) => api.post('/grants/wizard', body),
  subAwardWizard: (body) => api.post('/sub-awards/wizard', body),
  deleteGrant: (id) => api.delete(`/grants/${id}`),
  subAwardWizardEdit: (id, body) => api.put(`/sub-awards/${id}/wizard`, body),
  createSubAwardSubmission: (id, body) => api.post(`/sub-awards/${id}/submissions`, body),
  indicatorRollup: (id) => api.get(`/grants/${id}/indicator-rollup`),
  deleteSubAward: (id) => api.delete(`/sub-awards/${id}`),
  allocationSummary: (grantId) => api.get(`/grants/${grantId}/allocation-summary`),
  // Reports module — Templates / Generated / AI Stories Library
  listFunderTemplates: () => api.get('/funder-reporting-templates'),
  listAllReports: () => api.get('/grants/reports/all'),
  listAllStories: (status) => api.get(`/grants/stories/all${q({ status })}`),
  // Phase 5 redesign — Monthly indicator matrix + Summary save + Exports
  reportIndicatorsMonthly: (rpId, range) => api.get(`/reporting-periods/${rpId}/indicators-monthly${q({ range })}`),
  generateStories: (rpId) => api.post(`/reporting-periods/${rpId}/generate-stories`, {}),
  generateReportSummary: (rpId) => api.post(`/reporting-periods/${rpId}/generate-summary`, {}),
  complianceOverview: (params = {}) => api.get(`/grants-compliance/overview${q(params)}`),
  markComplianceReviewed: (id) => api.post(`/compliance/${id}/mark-reviewed`, {}),
  pushStoryToExecutive: (storyId) => api.post(`/grants/stories/${storyId}/push-to-executive`, {}),
  listExecutiveStories: (grantId) => api.get(`/executive/impact-stories${q({ grant_id: grantId })}`),
  removeExecutiveStory: (id) => api.delete(`/executive/impact-stories/${id}`),
  dataFlow: (lens) => api.get(`/grants-dashboard/data-flow${q({ lens })}`),
  importSubAwardSubmission: async (subAwardId, submissionId, sheet, file) => {
    const token = localStorage.getItem('district_token') || '';
    const fd = new FormData();
    fd.append('file', file);
    const res = await fetch(`${BACKEND}/api/sub-awards/${subAwardId}/submissions/${submissionId}/import?sheet=${encodeURIComponent(sheet)}`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}` },
      body: fd,
    });
    const json = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(json?.detail || `Import failed (${res.status})`);
    return json;
  },
  saveReportSummary: (rpId, summary) => api.put(`/reporting-periods/${rpId}/summary`, { summary }),
  downloadReportExport: async (rpId, kind) => {
    const token = localStorage.getItem('district_token') || '';
    const res = await fetch(`${BACKEND}/api/reporting-periods/${rpId}/export/${kind}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!res.ok) throw new Error(`Export failed (${res.status})`);
    const blob = await res.blob();
    // Pull filename out of Content-Disposition, fallback to generated name
    const cd = res.headers.get('Content-Disposition') || '';
    const m = cd.match(/filename="([^"]+)"/);
    const filename = m?.[1] || `report.${kind}`;
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = filename; a.style.display = 'none';
    document.body.appendChild(a); a.click(); a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
    return filename;
  },
};

export const notificationsApi = {
  list: (filters) => api.get(`/notifications${q(filters)}`),
  unreadCount: () => api.get(`/notifications/unread-count`),
  markRead: (id) => api.post(`/notifications/${id}/read`, {}),
  dismiss: (id) => api.post(`/notifications/${id}/dismiss`, {}),
  markAllRead: () => api.post(`/notifications/mark-all-read`, {}),
};

export const adminApi = {
  resetWritable: (adminToken) => api.post('/admin/reset-writable', undefined, adminToken ? { 'X-Admin-Token': adminToken } : {}),
  // Demo helper: silently authenticates as Super Admin then resets writable
  // collections. Used by the "Reset Demo Data" button on the login screens.
  // Restores the previous auth state on completion so the demo user isn't
  // accidentally signed in as admin.
  demoReset: async () => {
    const ADMIN_TOKEN = 'district-demo-admin-token';
    const prevToken = localStorage.getItem('district_token');
    const prevUser = localStorage.getItem('district_user');
    try {
      const auth = await api.post('/auth/admin/login', { email: 'admin@district.app', password: 'demo' });
      if (!auth?.success || !auth?.token) throw new Error(auth?.error || 'Admin auth failed');
      localStorage.setItem('district_token', auth.token);
      const result = await api.post('/admin/reset-writable', undefined, { 'X-Admin-Token': ADMIN_TOKEN });
      return result;
    } finally {
      // Restore prior session (or clear if there wasn't one)
      if (prevToken) localStorage.setItem('district_token', prevToken);
      else localStorage.removeItem('district_token');
      if (prevUser) localStorage.setItem('district_user', prevUser);
      else localStorage.removeItem('district_user');
    }
  },
};
